import type { EventStatus } from "@/api/events.service";

/**
 * The wording the event form uses to say what pressing Save will do to this event's place on the
 * public events page.
 *
 * A status select alone does not tell a staff user whether the event is on the site right now:
 * the field shows what they have just picked, not what content-api has stored. Reading a badge
 * beside the form's footer does — and reading it next to the button that would apply it is what
 * makes the consequence obvious before the click, not after.
 *
 * The saved value and the form value are kept apart on purpose. If the badge tracked the select
 * alone it would start lying the instant someone changed it — claiming an event was already off
 * the site when nothing had been saved. So an unsaved change reads as what is true now plus the
 * action that would apply it: "Live · save to hide".
 *
 * Only `published` reaches the public page; `draft` and `cancelled` differ editorially but are
 * identical as far as public visibility goes. That is why the footer keys off visibility rather
 * than the status name — there is one sentence per transition, not one per status.
 *
 * "Live" and "Hidden" are deliberately the Hero Banner screen's words, so the two Events screens
 * read as one product.
 */

export type EventVisibilityTone = "live" | "hidden" | "pending" | "unsaved";

export type EventVisibility = {
  tone: EventVisibilityTone;
  /** The status chip beside the "Visibility" heading. Short enough to read at a glance. */
  badge: string;
  /** One sentence: what pressing Save will do. */
  consequence: string;
};

export function describeEventSaveConsequence({
  saved,
  pending,
}: {
  /** The status content-api has stored. `null` for an event that has never been saved. */
  saved: EventStatus | null;
  /** The status currently selected in the form, which is only stored once the form is saved. */
  pending: EventStatus;
}): EventVisibility {
  // An event that has never been saved is not on the public page, so `null` counts as not visible.
  const wasVisible = saved === "published";
  const willBeVisible = pending === "published";

  const consequence = willBeVisible
    ? wasVisible
      ? "Saving will keep this event on the public events page."
      : "Saving will put this event on the public events page."
    : wasVisible
      ? "Saving will take this event off the public events page."
      : "Saving will keep this event off the public events page.";

  if (saved === null) {
    return { tone: "unsaved", badge: "Not saved yet", consequence };
  }

  if (wasVisible && willBeVisible) {
    return { tone: "live", badge: "Live", consequence };
  }

  if (wasVisible && !willBeVisible) {
    return { tone: "pending", badge: "Live · save to hide", consequence };
  }

  if (!wasVisible && willBeVisible) {
    return { tone: "pending", badge: "Hidden · save to publish", consequence };
  }

  return { tone: "hidden", badge: "Hidden", consequence };
}
